import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Check } from "@phosphor-icons/react";
import "../NivTasksPage.css";

const POPOVER_WIDTH = 190;
const OPTION_HEIGHT = 41; // option row + gap
const MOBILE_BREAKPOINT = 768;

/**
 * Generic colored-option dropdown for status / priority / type cells.
 * Rendered in a portal (fixed positioning) so it's never clipped by
 * group containers with overflow:hidden.
 * Desktop: anchored under the clicked pill. Mobile: bottom sheet (CSS).
 *
 * @param {Array}    options    - [{ id, label, color }]
 * @param {string}   value      - currently selected option id
 * @param {Element}  anchorEl   - the pill button element to anchor to
 * @param {function} onSelect   - (optionId|null) => void
 * @param {function} onClose    - close handler
 * @param {string}   [title]    - sheet title (shown on mobile)
 * @param {boolean}  [allowClear] - show a "None" option that selects null
 */
function OptionPopover({ options, value, anchorEl, onSelect, onClose, title, allowClear }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Close on scroll/resize so the fixed popover doesn't drift from its anchor
  useEffect(() => {
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  const positionStyle = useMemo(() => {
    if (window.innerWidth <= MOBILE_BREAKPOINT || !anchorEl) {
      return undefined; // mobile bottom sheet is positioned by CSS
    }
    const rect = anchorEl.getBoundingClientRect();
    const estimatedHeight = (options.length + (allowClear ? 1 : 0)) * OPTION_HEIGHT + 16;

    let left = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - POPOVER_WIDTH - 8));

    const openUp = rect.bottom + 4 + estimatedHeight > window.innerHeight - 8;
    return openUp
      ? { left, bottom: window.innerHeight - rect.top + 4 }
      : { left, top: rect.bottom + 4 };
  }, [anchorEl, options.length, allowClear]);

  const handleSelect = (optionId) => {
    onSelect(optionId);
    onClose();
  };

  return createPortal(
    <div dir="ltr">
      <div className="nivtasks-popover-backdrop" onClick={onClose} />
      <div className="nivtasks-popover" style={positionStyle} role="listbox">
        {title && <div className="nivtasks-popover-title">{title}</div>}
        <div className="nivtasks-popover-options">
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`nivtasks-popover-option ${opt.id === value ? "is-selected" : ""}`}
              style={{ backgroundColor: opt.color }}
              onClick={() => handleSelect(opt.id)}
            >
              <span className="nivtasks-popover-option-label">{opt.label}</span>
              {opt.id === value && <Check size={14} weight="bold" />}
            </button>
          ))}
          {allowClear && (
            <button
              type="button"
              className={`nivtasks-popover-option nivtasks-popover-option-clear ${!value ? "is-selected" : ""}`}
              onClick={() => handleSelect(null)}
            >
              <span className="nivtasks-popover-option-label">None</span>
              {!value && <Check size={14} weight="bold" />}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default OptionPopover;
